import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { EventService } from '@app/modules/events/services/event.service'
import { FilterButtonComponent } from '@app/v2/shared/components/filter-button/filter-button.component'
import { FilterOptionComponent } from '@app/v2/shared/components/filter-option/filter-option.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { MobileAccordionComponent } from '@app/v2/shared/components/mobile-accordion/mobile-accordion.component'
import { MobileFilterDrawerComponent } from '@app/v2/shared/components/mobile-filter-drawer/mobile-filter-drawer.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { MobileFilterDrawerService } from '@app/v2/shared/services/mobile-filter-drawer.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { InfiniteScrollModule } from 'ngx-infinite-scroll'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { debounceTime, distinctUntilChanged, EMPTY, finalize, Subject, switchMap, tap } from 'rxjs'
import { Webhook } from '../../enums/webhooks.model'
import { WebhookIcons } from '../../models/organization-settings.model'
import { OrganizationService } from '../../services/organization.service'
import { WebhookService } from '../../services/webhook.service'
import { CreateWebhookModalComponent } from './create-webhook-modal/create-webhook-modal.component'
import { WebhookCardComponent, WebhookEventOption } from './webhook-card/webhook-card.component'
import { WebhookLogsComponent } from './webhook-logs/webhook-logs.component'

@Component({
  selector: 'app-webhooks',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    InfiniteScrollModule,
    CreateWebhookModalComponent,
    WebhookCardComponent,
    DeleteModalComponent,
    SelectInputComponent,
    FilterButtonComponent,
    MobileFilterDrawerComponent,
    MobileAccordionComponent,
    FilterOptionComponent,
    SafeDomPipe,
    NgxSkeletonLoaderModule
  ],
  templateUrl: './webhooks.component.html',
  styleUrls: ['./webhooks.component.scss'],
  providers: [WebhookService, MobileFilterDrawerService]
})
export class WebhooksComponent implements OnInit {
  public readonly webHooks = signal<Webhook[]>([])
  public readonly events = signal<WebhookEventOption[]>([])
  public readonly isLoadingEvents = signal(false)
  public readonly isLoadingWebhooks = signal(false)
  public readonly isLoadingMore = signal(false)
  public readonly searchQuery = signal('')
  public readonly searchControl = new FormControl('', { nonNullable: true })
  public readonly eventFilterControl = new FormControl<number | null>(null)
  public readonly createModalId = 'create-webhook-modal'
  public readonly editModalId = 'edit-webhook-modal'
  public readonly deleteWebhookModalId = 'delete-webhook-modal'
  public readonly pendingDeletion = signal<Webhook | null>(null)
  public readonly editingWebhook = signal<Webhook | null>(null)
  public readonly mobileEventFilterDraft = signal<number | null>(null)
  public readonly eventFilterId = signal<number | null>(null)

  public readonly hasActiveFilters = computed(() => !!this.searchQuery().trim() || this.eventFilterId() != null)
  public readonly mobileActiveFilterCount = computed(() => (this.eventFilterId() != null ? 1 : 0))

  public readonly deleteWebhookConfirmMessage = computed(() => {
    const webhook = this.pendingDeletion()
    if (!webhook) return ''
    const eventName = this.events().find((event) => event.id === webhook.event_id)?.name ?? webhook.event_slug
    return this._translate.instant('v2.orgSettings.webhooks.deleteWebhookConfirm', { name: webhook.name, eventName })
  })

  public readonly showInitialEmptyState = computed(() => !this.isLoadingWebhooks() && this.webHooks().length === 0 && !this.hasActiveFilters())

  public readonly infiniteScrollDisabled = computed(() => !this._hasMore() || this.isLoadingWebhooks() || this.isLoadingMore())

  public readonly emptyStateIcon = WebhookIcons.getIcon().emptyState

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _authService = inject(AuthService)
  private readonly _eventService = inject(EventService)
  private readonly _modalService = inject(ModalService)
  private readonly _wizardService = inject(WizardService)
  private readonly _alertService = inject(AlertService)
  private readonly _translate = inject(TranslateService)
  private readonly _webhookService = inject(WebhookService)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _mobileFilterDrawer = inject(MobileFilterDrawerService)

  private _page = 1
  private readonly _hasMore = signal(true)
  private readonly _perPage = 10
  private readonly _load$ = new Subject<boolean>()

  ngOnInit(): void {
    this._setupWebhookLoading()
    this._loadEvents()

    this.searchControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      this.searchQuery.set(value)
      this._load$.next(true)
    })

    this.eventFilterControl.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      this.eventFilterId.set(value)
      this._load$.next(true)
    })

    this._load$.next(true)
  }

  /**
   * Opens the create webhook modal.
   */
  public openCreateWebhookModal(): void {
    this._openModal(this.createModalId)
  }

  /**
   * Closes the create webhook modal.
   */
  public closeCreateWebhookModal(): void {
    this._closeModal(this.createModalId)
  }

  /**
   * Whether a modal with the given id is currently open.
   * @param id {string}
   * @returns {boolean}
   */
  public isModalOpened(id: string): boolean {
    return this._modalService.isModalOpen(id)
  }

  /**
   * Reloads webhooks after a successful create and closes the modal.
   */
  public onWebhookCreated(): void {
    this._load$.next(true)
    this.closeCreateWebhookModal()
  }

  /**
   * Applies a webhook update from the card toggle or the edit modal.
   * @param updatedWebhook {Webhook}
   */
  public onWebhookUpdated(updatedWebhook: Webhook): void {
    this.webHooks.update((webhooks) => webhooks.map((webhook) => (webhook.id === updatedWebhook.id ? updatedWebhook : webhook)))
  }

  /**
   * Opens the edit webhook modal for the selected webhook.
   * @param webhook {Webhook}
   */
  public onEditRequested(webhook: Webhook): void {
    this.editingWebhook.set(webhook)
    this._openModal(this.editModalId)
  }

  /**
   * Closes the edit webhook modal.
   */
  public closeEditWebhookModal(): void {
    this._closeModal(this.editModalId)
    this.editingWebhook.set(null)
  }

  /**
   * Applies an edited webhook and closes the edit modal.
   * @param updatedWebhook {Webhook}
   */
  public onWebhookEdited(updatedWebhook: Webhook): void {
    this.onWebhookUpdated(updatedWebhook)
    this.closeEditWebhookModal()
  }

  public onDeleteRequested(webhook: Webhook): void {
    this.pendingDeletion.set(webhook)
    if (!this._modalService.isModalOpen(this.deleteWebhookModalId)) {
      this._modalService.toggleModal(this.deleteWebhookModalId)
    }
  }

  public onLogsRequested(webhook: Webhook): void {
    this._wizardService.open({
      component: WebhookLogsComponent,
      componentInputs: { webhook },
      headerConfig: {
        breadcrumbs: [
          this._translate.instant('v2.orgSettings.organizationSettings'),
          this._translate.instant('v2.orgSettings.webhooks.webhooks'),
          this._translate.instant('v2.orgSettings.webhooks.webhookLogs')
        ]
      }
    })
  }

  public confirmWebhookDeletion(): void {
    const webhook = this.pendingDeletion()
    if (!webhook) return

    this._webhookService
      .deleteWebhook(webhook.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.webHooks.update((webhooks) => webhooks.filter((item) => item.id !== webhook.id))
          this.pendingDeletion.set(null)
          if (this._modalService.isModalOpen(this.deleteWebhookModalId)) {
            this._modalService.toggleModal(this.deleteWebhookModalId)
          }
          this._alertService.alert(Alert.SUCCESS, `${webhook.name}/${webhook.event_slug} ${this._translate.instant('v2.orgSettings.webhooks.deleteWebhookSuccess')}`)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.webhooks.deleteWebhookError'))
        }
      })
  }

  public cancelWebhookDeletion(): void {
    this.pendingDeletion.set(null)
    if (this._modalService.isModalOpen(this.deleteWebhookModalId)) {
      this._modalService.toggleModal(this.deleteWebhookModalId)
    }
  }

  public onWebhooksListScroll(): void {
    if (this.infiniteScrollDisabled()) return
    this._load$.next(false)
  }

  public onMobileFilterOpen(): void {
    this.mobileEventFilterDraft.set(this.eventFilterId())
  }

  public setMobileEventFilterDraft(eventId: number | null): void {
    this.mobileEventFilterDraft.set(eventId)
  }

  public onMobileFilterReset(): void {
    this.mobileEventFilterDraft.set(null)
  }

  public onMobileFilterApply(): void {
    this.eventFilterControl.setValue(this.mobileEventFilterDraft())
    this._mobileFilterDrawer.close()
  }

  public onMobileFilterClosed(): void {
    this.mobileEventFilterDraft.set(this.eventFilterId())
  }

  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  /**
   * Opens a modal if it is not already open.
   * @param id {string}
   */
  private _openModal(id: string): void {
    if (!this._modalService.isModalOpen(id)) this._modalService.toggleModal(id)
  }

  /**
   * Closes a modal if it is open.
   * @param id {string}
   */
  private _closeModal(id: string): void {
    if (this._modalService.isModalOpen(id)) this._modalService.toggleModal(id)
  }

  private _setupWebhookLoading(): void {
    this._load$
      .pipe(
        tap((reset) => {
          if (reset) {
            this._page = 1
            this._hasMore.set(true)
            this.webHooks.set([])
            this.isLoadingWebhooks.set(true)
            this.isLoadingMore.set(false)
            return
          }

          if (!this._hasMore()) return
          this.isLoadingMore.set(true)
        }),
        switchMap(() => {
          if (!this._hasMore()) return EMPTY

          const organizationId = this._authService.currentWorkspace?.id
          if (!organizationId) return EMPTY

          return this._webhookService
            .getWebhooks(organizationId, {
              page: this._page,
              per_page: this._perPage,
              name: this.searchControl.value,
              event_id: this.eventFilterControl.value
            })
            .pipe(
              finalize(() => {
                this.isLoadingWebhooks.set(false)
                this.isLoadingMore.set(false)
              })
            )
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (result) => {
          this.webHooks.update((current) => (result.current_page === 1 ? result.items : [...current, ...result.items]))
          this._hasMore.set(result.current_page < result.last_page)
          this._page = result.current_page + 1
        },
        error: (error) => {
          this._hasMore.set(false)
          if (this._page === 1) this.webHooks.set([])
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.webhooks.loadWebhooksError'))
        }
      })
  }

  private _loadEvents(): void {
    this.isLoadingEvents.set(true)
    this._eventService
      .getEventsExcept(1)
      .pipe(
        finalize(() => this.isLoadingEvents.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => this.events.set(this._normalizeEvents(response)),
        error: (error) => {
          this.events.set([])
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.webhooks.loadEventsError'))
        }
      })
  }

  private _normalizeEvents(response: unknown): WebhookEventOption[] {
    const rawEvents = Array.isArray(response) ? response : (response as { data?: unknown[] })?.data ?? []

    return rawEvents
      .map((event) => {
        const record = event as { id?: number | string; name?: string; slug?: string }
        const id = Number(record.id)
        const name = record.name?.trim() ?? ''
        const slug = record.slug?.trim() ?? ''

        if (Number.isNaN(id) || !name) return null

        return { id, name, slug }
      })
      .filter((event): event is WebhookEventOption => event !== null)
  }
}
